/* Presentation and enquiry controls only. Native Broadcast selects and cart remain authoritative. */
(() => {
  'use strict';
  if (window.HOQMobileProduct) { window.HOQMobileProduct.mount(); return; }
  const mobileMedia = matchMedia('(max-width: 749px)');
  // Shared presentation controls now support the matching desktop companion.
  const media = matchMedia('(min-width: 0px)');
  const roots = new Map();
  const parse = (node) => { try { return JSON.parse(node?.textContent || '{}'); } catch (_) { return {}; } };
  const digits = (value) => String(value || '').replace(/\D/g, '');
  const purity = (value) => String(value || '').match(/\b(24|22|18|14|10|9)\s*k(?:t)?\b/i)?.[1];

  // Match the purity aliases without changing Shopify's actual option values.
  const platinum950 = (value) => /^(?:950|p950|pt950|platinum950|950platinum)$/.test(
    String(value || '').toLowerCase().replace(/[\s-]+/g, '')
  );

  class MobileProduct {
    constructor(root) {
      this.root = root;
      this.config = parse(root.querySelector('[data-hoq-mobile-config]'));
      this.state = parse(root.querySelector('[data-hoq-mobile-state]'));
      this.metalTooltips = new Map();
      this.gallery = root.querySelector('product-images');
      this.onGalleryScroll = () => this.progress();
      this.gallery?.querySelector('[data-product-media-list]')?.addEventListener('scroll', this.onGalleryScroll, { passive: true });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(root.querySelector('.form__width') || root, { childList: true, subtree: true });
      if (this.gallery) this.observer.observe(this.gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-active-media', 'class'] });
      this.refresh();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.refresh(); });
    }

    update(event) {
      if (String(event?.data?.sectionId) !== String(this.root.dataset.section)) return;
      const html = event.data.html;
      const newState = html?.querySelector?.('[data-hoq-mobile-state]');
      if (newState) this.state = parse(newState);
      else if (event.data.variant) {
        const v = event.data.variant;
        const saved = this.config.variants?.find((item) => String(item.id) === String(v.id));
        this.state = { ...this.state, ...saved, id: v.id, available: v.available };
      }
      this.schedule();
    }

    selection() {
      return Array.from(this.root.querySelectorAll('[data-option-position]')).filter((group) => !group.closest('[hidden]') && group.getClientRects().length > 0).map((group) => {
        const radio = group.querySelector('input[type="radio"]:checked');
        const input = group.querySelector('[data-popout-input]');
        const select = group.querySelector('select');
        return { group, position: Number(group.dataset.optionPosition) - 1, value: radio?.value || input?.value || select?.value || '' };
      });
    }

    variantURL() {
      const url = new URL(this.config.url || this.root.dataset.url, location.origin);
      const id = this.root.querySelector('.product-variant-id')?.value || this.state.id;
      if (/^\d+$/.test(String(id || ''))) url.searchParams.set('variant', id);
      return url.href;
    }

    syncSelectedVariant() {
      // The native cart form's selected ID is authoritative. Some theme/app
      // updates replace inputs without dispatching the variant pub/sub event.
      const input = this.root.querySelector('product-form input[name="id"]') || this.root.querySelector('.product-variant-id');
      if (!input || String(this.state.id) === input.value) return;
      const variant = this.config.variants?.find((item) => String(item.id) === input.value);
      if (variant) this.state = { ...variant, netWeight: null };
    }

    refresh() {
      if (!this.root.isConnected) return;
      this.root.classList.toggle('hoq-mobile-active', mobileMedia.matches);
      document.body.classList.toggle('hoq-mobile-page', mobileMedia.matches && !!document.querySelector('.hoq-mobile-product'));
      document.body.classList.toggle('hoq-desktop-page', !mobileMedia.matches);
      if (!media.matches) {
        this.restoreWishlist();
        this.restoreMetalTooltips();
        this.restoreStoreAvailability();
        this.root.querySelector('[data-hoq-ring-guide]')?.close();
        const dialog = document.getElementById('HOQVideoCall-' + this.root.dataset.section);
        if (dialog?.open) dialog.close();
        return;
      }
      this.syncSelectedVariant();
      this.options();
      this.delivery();
      this.moveStoreAvailability();
      this.ringSizeGuide();
      // Broadcast also renders a form-associated variant input outside the cart form.
      // Keep it in sync so FormData cannot retain the first variant after a selection.
      const chosenId = this.root.querySelector('.product-variant-id')?.value || '';
      this.root.querySelectorAll('input[name="id"][form]').forEach((input) => {
        if (input.value !== chosenId) input.value = chosenId;
      });
      if (mobileMedia.matches) this.decorateSections();
      this.decoratePriceBreakup();
      this.moveWishlist();
      this.progress();
      const link = this.root.querySelector('[data-hoq-mobile-whatsapp]');
      if (link) link.href = this.whatsappURL('Hi, I would like to know more about ' + this.config.title + '\n' + this.variantURL());
    }

    options() {
      const selected = this.selection();
      const currentPurity = selected.map((item) => purity(item.value)).find(Boolean);
      selected.forEach(({ group, position, value }) => {
        const legendValue = group.querySelector('[data-hoq-selected-value]');
        if (legendValue) {
          const isPlatinum = group.dataset.hoqOptionKind === 'metal' && platinum950(value);
          const isPurity = /purity|karat|carat/i.test(group.dataset.hoqOptionName || '');
          const goldPurity = isPurity
            ? value.trim().match(/^(18|14|9)\s*k(?:t)?(?:\s+gold)?$/i)
            : null;
          const displayValue = isPlatinum
            ? 'Platinum'
            : goldPurity ? `${goldPurity[1]}K Gold` : value;
          if (legendValue.textContent !== displayValue) legendValue.textContent = displayValue;
          legendValue.style.textTransform = isPlatinum || goldPurity ? 'none' : '';
        }
        // Follow the picker from left to right: purity -> colour -> size.
        // A default out-of-stock size must not hide stocked purity/colour paths.
        // Size availability still matches the selected purity AND colour.
        const compatibleStock = (candidate) => (this.config.variants || []).some((v) =>
          v.available && v.quantity > 0 && v.options?.[position] === candidate &&
          selected.every((other) => other.position >= position || !other.value || v.options?.[other.position] === other.value)
        );
        group.querySelectorAll('.radio__button, .swatch__button').forEach((button) => {
          const input = button.querySelector('input');
          if (!input) return;
          const stocked = compatibleStock(input.value);
          button.classList.toggle('hoq-option-instock', stocked);
          if (group.dataset.hoqOptionKind !== 'metal') return;
          const low = input.value.toLowerCase();
          const background = /rose|pink/.test(low) ? 'linear-gradient(135deg,#dbac93,#efc1a2)' : /yellow/.test(low) ? 'linear-gradient(135deg,#edc960,#f8df89)' : (/white|platinum|silver/.test(low) || platinum950(input.value)) ? 'linear-gradient(135deg,#c5c5c5,#e6e6e6)' : '#d5d5d5';
          if (button.style.getPropertyValue('--hoq-metal-bg') !== background) button.style.setProperty('--hoq-metal-bg', background);
          let badge = button.querySelector('.hoq-mobile-variant-stock');
          if (stocked && !badge) {
            badge = document.createElement('span');
            badge.className = 'hoq-mobile-only hoq-mobile-variant-stock';
            badge.textContent = 'In-stock options available';
            button.appendChild(badge);
          } else if (!stocked && badge) badge.remove();
          const caption = button.querySelector('.hoq-metal-caption');
          if (caption) {
            const label = platinum950(input.value) ? 'P950' : caption.dataset.hoqBasePurity || (currentPurity ? currentPurity + 'K' : '');
            if (caption.textContent !== label) caption.textContent = label;
          }
          const label = button.querySelector('label');
          if (!this.metalTooltips.has(button)) this.metalTooltips.set(button, { tooltip: button.label, title: label?.getAttribute('title') });
          if (button.tagName === 'TOOLTIP-COMPONENT' && typeof button.label === 'string') {
            const saved = this.metalTooltips.get(button);
            if (saved.tooltip == null) saved.tooltip = button.label;
            button.label = '';
          }
          label?.removeAttribute('title');
        });
        group.querySelectorAll('.select-popout__item').forEach((item) => {
          const link = item.querySelector('[data-popout-option]');
          if (!link) return;
          let badge = link.querySelector('.hoq-mobile-size-stock');
          if (compatibleStock(link.dataset.value) && !badge) {
            badge = document.createElement('span'); badge.className = 'hoq-mobile-size-stock'; badge.textContent = 'In Stock'; link.appendChild(badge);
          } else if (!compatibleStock(link.dataset.value) && badge) badge.remove();
        });
        if (group.dataset.hoqOptionKind === 'size') {
          group.querySelector('.select-popout__toggle')?.classList.toggle('hoq-selected-instock', !!this.state.available && this.state.quantity > 0);
        }
      });
      this.root.querySelectorAll('.hoq-mobile-stock-label').forEach((status) => status.remove());
    }

    restoreMetalTooltips() {
      for (const [button, saved] of this.metalTooltips) {
        if (!button.isConnected) continue;
        if (button.tagName === 'TOOLTIP-COMPONENT' && saved.tooltip != null) button.label = saved.tooltip;
        const label = button.querySelector('label');
        if (label && saved.title != null) label.setAttribute('title', saved.title);
      }
      this.metalTooltips.clear();
    }

    delivery() {
      const node = this.root.querySelector('[data-hoq-delivery-text]');
      if (node) {
        const inStock = this.state.available && this.state.quantity > 0;
        const text = !this.state.available ? 'This combination is currently unavailable.' : inStock ? 'InStock | Ships In 2 Days' : 'Made to Order | Estimated ship date: ' + node.dataset.hoqShipDate;
        if (node.textContent !== text) node.textContent = text;
        const pickup = this.root.querySelector('[data-hoq-pickup-inline]');
        // Reuse the store drawer's own trigger and handlers; offer pickup only
        // when this selected variant is stocked and the drawer exists.
        if (pickup) pickup.hidden = !inStock || !this.root.querySelector('[data-ps-sa-open]');
      }
      const weight = this.root.querySelector('[data-hoq-net-weight]');
      if (weight) {
        const value = this.state.netWeight;
        weight.closest('[data-hoq-net-weight-row]').hidden = value == null || value === '';
        if (value != null && value !== '') {
          const text = Number(value).toFixed(2) + ' g';
          if (weight.textContent !== text) weight.textContent = text;
        }
      }
    }

    decorateSections() {
      const mainSection = this.root.closest('.shopify-section');
      const container = mainSection?.parentElement;
      if (!container) return;
      Array.from(container.children).forEach((section) => {
        if (!section.classList.contains('shopify-section') || section === mainSection) return;
        const details = section.querySelectorAll('.accordion-section details.accordion, collapsible-elements > details.accordion');
        details.forEach((detail) => {
          section.classList.add('hoq-mobile-lower-section');
          detail.classList.add('hoq-mobile-lower');
          const title = detail.querySelector(':scope > summary')?.textContent.trim().toLowerCase() || '';
          if (title.includes('materials')) section.classList.add('hoq-mobile-materials');
          if (/^faqs?\b/.test(title)) detail.classList.add('hoq-mobile-faq-accordion');
        });
        if (section.querySelector('related-products, product-recommendations, [data-section-type="related"]')) section.classList.add('hoq-mobile-related');
        section.querySelectorAll('.pvt-tab-content').forEach((content, i) => {
          let heading = content.querySelector('.hoq-mobile-faq-heading');
          if (!heading) {
            heading = document.createElement('button'); heading.type = 'button'; heading.className = 'hoq-mobile-only hoq-mobile-faq-heading';
            heading.textContent = content.dataset.title || 'FAQ';
            const body = content.querySelector('.pvt-inner');
            if (body) { body.id = body.id || 'HOQFaq-' + this.root.dataset.section + '-' + i; heading.setAttribute('aria-controls', body.id); }
            content.prepend(heading);
          }
          heading.setAttribute('aria-expanded', String(content.classList.contains('is-active')));
        });
      });
    }

    moveWishlist() {
      const target = this.root.querySelector('.product__title__wrapper');
      // The native gallery repeats this ID for every slide. Move only one node.
      const candidates = Array.from(this.root.querySelectorAll('#wishlist-hero-product-page-button'));
      const populated = candidates.find((node) => node.childElementCount > 0);
      let wishlist = this.wishlist?.isConnected ? this.wishlist : candidates[0];
      // The app can initialise a gallery placeholder after our script has loaded.
      if (wishlist && !wishlist.childElementCount && populated && populated !== wishlist) {
        this.restoreWishlist();
        wishlist = populated;
      }
      if (!target || !wishlist || target.contains(wishlist)) return;
      this.wishlist = wishlist;
      this.wishlistMarker = document.createComment('HOQ wishlist original position');
      wishlist.before(this.wishlistMarker);
      wishlist.classList.add('hoq-mobile-wishlist');
      target.appendChild(wishlist);
    }

    decoratePriceBreakup() {
      this.root.querySelectorAll('#hoq-inner').forEach((inner) => {
        const summary = inner.querySelector(':scope > .hoq-summary');
        if (!summary || inner.querySelector(':scope > .hoq-mobile-pb-components')) return;
        const components = document.createElement('div');
        components.className = 'hoq-mobile-pb-components';
        inner.insertBefore(components, inner.firstChild);
        while (components.nextSibling && components.nextSibling !== summary) components.appendChild(components.nextSibling);
        const mobileValue = (cell, text) => {
          if (!cell) return;
          const desktop = document.createElement('span');
          desktop.className = 'hoq-pb-desktop-value';
          while (cell.firstChild) desktop.appendChild(cell.firstChild);
          const mobile = document.createElement('span');
          mobile.className = 'hoq-mobile-pb-value';
          mobile.textContent = text;
          cell.append(desktop, mobile);
        };
        mobileValue(components.querySelector('.hoq-gold-table th:nth-child(3)'), 'Weight\n(Gm)');
        components.querySelectorAll('.hoq-dia-table th:nth-child(2)').forEach((cell) => {
          if (/colour\s*\/\s*clarity/i.test(cell.textContent)) mobileValue(cell, 'Colour/\nClarity');
        });
        components.querySelectorAll('.hoq-gold-table tbody tr').forEach((row) => {
          const weight = row.cells[2];
          const value = parseFloat(weight?.textContent);
          if (Number.isFinite(value)) mobileValue(weight, value.toFixed(2));
          const rate = row.cells[1];
          if (/\/Gm/i.test(rate?.textContent || '')) mobileValue(rate, rate.textContent.trim().replace(/\/Gm/ig, ''));
        });
        components.querySelectorAll('.hoq-dia-table tbody tr').forEach((row) => {
          const carat = row.cells[2];
          const value = parseFloat(carat?.textContent);
          if (Number.isFinite(value)) mobileValue(carat, value.toFixed(2));
        });
      });
    }

    restoreWishlist() {
      if (this.wishlistMarker?.isConnected && this.wishlist?.isConnected) {
        this.wishlistMarker.replaceWith(this.wishlist); this.wishlist.classList.remove('hoq-mobile-wishlist'); this.wishlistMarker = null;
      }
    }

    moveStoreAvailability() {
      const panel = this.root.querySelector('[data-hoq-service-panel="delivery"]');
      const block = this.root.querySelector('[data-hoq-role="ps_store_availability"]');
      if (!panel || !block || panel.contains(block)) return;
      this.restoreStoreAvailability();
      this.availabilityBlock = block;
      this.availabilityMarker = document.createComment('HOQ store availability desktop position');
      block.before(this.availabilityMarker);
      panel.appendChild(block);
    }

    restoreStoreAvailability() {
      if (this.availabilityMarker?.parentNode && this.availabilityBlock?.parentNode) {
        this.availabilityMarker.replaceWith(this.availabilityBlock);
      }
      this.availabilityMarker = null;
      this.availabilityBlock = null;
    }

    ringSizeGuide() {
      const dialog = this.root.querySelector('[data-hoq-ring-guide]');
      if (!dialog) return;
      const group = this.root.querySelector('[data-hoq-option-kind="size"]');
      if (!group) return;
      group.classList.add('hoq-has-ring-guide');
      if (group.querySelector('[data-hoq-ring-guide-open]')) return;
      const target = group.querySelector('.select__fieldset') || group.querySelector('.radio__legend__label') || group;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hoq-mobile-only hoq-mobile-ring-guide-link';
      button.dataset.hoqRingGuideOpen = dialog.id;
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-controls', dialog.id);
      button.textContent = 'Size Guide';
      target.appendChild(button);
    }

    progress() {
      const slides = this.gallery?.querySelector('[data-product-media-list]');
      if (!mobileMedia.matches || !slides) return;
      let progress = this.gallery.querySelector('.hoq-mobile-gallery-progress');
      if (!progress) { progress = document.createElement('div'); progress.className = 'hoq-mobile-only hoq-mobile-gallery-progress'; progress.setAttribute('aria-hidden', 'true'); progress.appendChild(document.createElement('span')); this.gallery.appendChild(progress); }
      const items = Array.from(slides.querySelectorAll('[data-slide]'));
      const total = items.length || 1;
      const active = items.findIndex((item) => item.classList.contains('media--active'));
      const selected = this.gallery.classList.contains('product__images--mobile-thumbs') ? Math.max(0, active) : Math.round(slides.scrollLeft / Math.max(1, slides.clientWidth));
      const index = Math.min(total - 1, Math.max(0, selected));
      progress.firstElementChild.style.width = (100 / total) + '%';
      progress.firstElementChild.style.transform = 'translateX(' + (index * 100) + '%)';
    }

    whatsappURL(message) { return 'https://wa.me/' + digits(this.config.whatsapp) + '?text=' + encodeURIComponent(message); }

    destroy() { this.observer.disconnect(); cancelAnimationFrame(this.frame); this.root.querySelector('[data-hoq-ring-guide]')?.close(); this.restoreWishlist(); this.restoreMetalTooltips(); this.restoreStoreAvailability(); }
  }

  function mount() {
    for (const [root, controller] of roots) if (!root.isConnected) { controller.destroy(); roots.delete(root); }
    document.querySelectorAll('.hoq-mobile-product').forEach((root) => { if (!roots.has(root)) roots.set(root, new MobileProduct(root)); else roots.get(root).schedule(); });
  }

  // Use the existing footer widget so its visitor tracking remains authoritative.
  // Delegation also covers product sections replaced after a variant change.
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-hoq-mobile-whatsapp]');
    if (!link || event.defaultPrevented) return;
    const widgetButton = document.getElementById('dt-chatButton');
    if (!widgetButton) return; // Retain the direct enquiry link if the widget is unavailable.
    event.preventDefault();
    widgetButton.click();
  });

  // Broadcast can initialise a tooltip while replacing the native variant picker.
  // Suppress its tooltip events on mobile, without suppressing option selection.
  function suppressMetalTooltip(event) {
    if (!media.matches || !event.target.closest?.('.hoq-mobile-product [data-hoq-option-kind="metal"] tooltip-component')) return;
    event.stopImmediatePropagation();
    document.querySelector('.tooltip-default')?.classList.remove('is-visible', 'is-hiding');
  }
  document.addEventListener('theme:tooltip:init', suppressMetalTooltip, true);
  document.addEventListener('mouseenter', suppressMetalTooltip, true);

  document.addEventListener('click', (event) => {
    if (!media.matches) return;
    const pickup = event.target.closest?.('[data-hoq-pickup-open]');
    if (!pickup) return;
    pickup.closest('.hoq-mobile-product')?.querySelector('[data-ps-sa-open]')?.click();
  });

  document.addEventListener('click', (event) => {
    if (!media.matches) return;
    const target = event.target.closest?.('[data-hoq-video-open], [data-hoq-video-close], [data-hoq-ring-guide-open], [data-hoq-ring-guide-close], [data-hoq-service-tab], [data-hoq-share-hint], .hoq-mobile-faq-heading');
    if (!target) return;
    if (target.matches('[data-hoq-ring-guide-open]')) {
      const dialog = document.getElementById(target.dataset.hoqRingGuideOpen);
      if (!dialog) return;
      dialog.hoqOpener = target;
      document.body.classList.add('hoq-ring-guide-open');
      if (!dialog.open) dialog.showModal();
      dialog.querySelector('[data-hoq-ring-guide-close]')?.focus({ preventScroll: true });
    } else if (target.matches('[data-hoq-ring-guide-close]')) target.closest('dialog')?.close();
    else if (target.matches('[data-hoq-video-open]')) {
      const dialog = document.getElementById(target.dataset.hoqVideoOpen);
      if (!dialog) return;
      dialog.hoqOpener = target;
      if (!dialog.open) dialog.showModal();
      dialog.querySelector('input')?.focus();
    } else if (target.matches('[data-hoq-video-close]')) target.closest('dialog')?.close();
    else if (target.matches('[data-hoq-service-tab]')) {
      const card = target.closest('.hoq-mobile-service-card');
      card.querySelectorAll('[data-hoq-service-tab]').forEach((tab) => { const active = tab === target; tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1; });
      card.querySelectorAll('[data-hoq-service-panel]').forEach((panel) => { panel.hidden = panel.dataset.hoqServicePanel !== target.dataset.hoqServiceTab; });
    } else if (target.matches('[data-hoq-share-hint]')) {
      const controller = roots.get(target.closest('.hoq-mobile-product'));
      if (!controller) return;
      const title = controller.config.title;
      const url = controller.variantURL();
      if (navigator.share) navigator.share({ title, text: 'This piece is on my wish list: ' + title, url }).catch(() => {});
      else window.open('https://wa.me/?text=' + encodeURIComponent('This piece is on my wish list: ' + title + '\n' + url), '_blank', 'noopener');
    } else {
      event.stopImmediatePropagation();
      const content = target.closest('.pvt-tab-content');
      const open = !content.classList.contains('is-active');
      content.classList.toggle('is-active', open);
      target.setAttribute('aria-expanded', String(open));
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.('[data-hoq-service-tab]');
    if (!media.matches || !tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = Array.from(tab.closest('.hoq-mobile-tabs').querySelectorAll('[data-hoq-service-tab]'));
    const i = tabs.indexOf(tab);
    const next = event.key === 'Home' ? tabs[0] : event.key === 'End' ? tabs.at(-1) : tabs[(i + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    next.click(); next.focus();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-hoq-video-form]');
    if (!form) return;
    event.preventDefault();
    const phone = form.elements.phone;
    const name = form.elements.name;
    const locationField = form.elements.location;
    phone.setCustomValidity(digits(phone.value).length >= 7 && digits(phone.value).length <= 15 ? '' : 'Enter a valid phone number.');
    name.setCustomValidity(name.value.trim() ? '' : 'Enter your name.');
    locationField.setCustomValidity(locationField.value.trim() ? '' : 'Enter your city or location.');
    if (!form.reportValidity()) return;
    const dialog = form.closest('dialog');
    const root = document.getElementById('MainProduct--' + dialog.dataset.hoqSection);
    const controller = roots.get(root);
    if (!controller) return;
    const chosen = controller.selection().map((item) => item.group.dataset.hoqOptionName + ': ' + item.value).join('\n');
    const message = 'Hi, I would like to book a video call.\n\nName: ' + name.value.trim() + '\nNumber: ' + phone.value.trim() + '\nLocation: ' + locationField.value.trim() + '\n\nProduct: ' + controller.config.title + (chosen ? '\n' + chosen : '') + '\n' + controller.variantURL();
    window.open(controller.whatsappURL(message), '_blank', 'noopener');
    const status = form.querySelector('[data-hoq-video-status]');
    status.hidden = false; status.textContent = 'Tap Send in WhatsApp to share your request with our team.';
  });
  document.addEventListener('input', (event) => { if (event.target.closest?.('[data-hoq-video-form]')) event.target.setCustomValidity?.(''); });
  document.addEventListener('change', (event) => {
    const root = event.target.closest?.('.hoq-mobile-product');
    const controller = roots.get(root);
    if (!controller) return;
    const input = root.querySelector('.product-variant-id');
    if (event.target === input) {
      const saved = controller.config.variants?.find((v) => String(v.id) === input.value);
      controller.state = saved ? { ...controller.state, ...saved } : { ...controller.state, id: null, available: false, quantity: 0, netWeight: null };
    }
    controller.schedule();
  });
  document.addEventListener('close', (event) => { if (event.target.matches?.('[data-hoq-video-dialog]')) event.target.hoqOpener?.focus({ preventScroll: true }); }, true);
  document.addEventListener('close', (event) => {
    if (!event.target.matches?.('[data-hoq-ring-guide]')) return;
    document.body.classList.remove('hoq-ring-guide-open');
    if (media.matches) {
      const opener = event.target.hoqOpener?.isConnected ? event.target.hoqOpener : event.target.closest('.hoq-mobile-product')?.querySelector('[data-hoq-ring-guide-open]');
      opener?.focus({ preventScroll: true });
    }
  }, true);
  document.addEventListener('click', (event) => {
    if (!event.target.matches?.('[data-hoq-ring-guide][open]')) return;
    const rect = event.target.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
  });
  mobileMedia.addEventListener('change', () => { for (const controller of roots.values()) controller.refresh(); });
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(mount));
  if (typeof subscribe === 'function' && window.theme?.PUB_SUB_EVENTS?.variantChange) subscribe(theme.PUB_SUB_EVENTS.variantChange, (event) => { mount(); for (const controller of roots.values()) controller.update(event); });
  window.HOQMobileProduct = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount); else mount();
})();
